import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';
import { emitEvent } from '@/utils/eventBus';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const DEFAULT_SIGNATURE_STYLE_ID = 'classic-serif';
const ASSIGNMENT_CONFIRMATION_TEMPLATE_KEY = 'assignment-confirmation-v1';
export const DEFAULT_SUPPLIER_DOCUMENT_CATEGORY = 'statutory_policies';
export const SUPPLIER_DOCUMENT_CATEGORIES = [
	{ value: 'statutory_policies', label: 'Statutory policies' },
	{ value: 'assignments', label: 'Assignments' },
	{ value: 'health_and_safety', label: 'Health and safety' },
];

const SUPPLIER_DOCUMENT_CATEGORY_VALUES = new Set(
	SUPPLIER_DOCUMENT_CATEGORIES.map(({ value }) => value),
);

export const normalizeSupplierDocumentCategory = (category) => {
	const value = String(category || '').trim().toLowerCase();
	return SUPPLIER_DOCUMENT_CATEGORY_VALUES.has(value)
		? value
		: DEFAULT_SUPPLIER_DOCUMENT_CATEGORY;
};

export const normalizeSupplierDocumentSourceType = (sourceType) =>
	String(sourceType || '').trim().toLowerCase() === 'template' ? 'template' : 'upload';

export const DOCUMENT_TRANSLATION_LANGUAGES = [
	{ language: 'uk', label: 'Ukrainian', shortLabel: 'UK' },
	{ language: 'pl', label: 'Polish', shortLabel: 'PL' },
	{ language: 'ro', label: 'Romanian', shortLabel: 'RO' },
	{ language: 'ru', label: 'Russian', shortLabel: 'RU' },
];

const normalizeErrorMessage = (error, fallback) => {
	return (
		error?.response?.data?.error?.message ||
		error?.response?.data?.message ||
		error?.message ||
		fallback
	);
};

const normalizeVersion = (version = {}) => ({
	id: version?.id ?? null,
	versionNumber: version?.versionNumber ?? 0,
	publishedAt: version?.publishedAt ?? null,
	fileHash: version?.fileHash ?? '',
	titleSnapshot: version?.titleSnapshot ?? '',
	descriptionSnapshot: version?.descriptionSnapshot ?? '',
	completionModeSnapshot:
		version?.completionModeSnapshot === 'bilateral_signature'
			? 'bilateral_signature'
			: 'read_only',
	requiresEffectiveDateSnapshot: Boolean(version?.requiresEffectiveDateSnapshot),
	ownerSignsFirstSnapshot: Boolean(version?.ownerSignsFirstSnapshot),
	executedCopyDownloadEnabledSnapshot: Boolean(version?.executedCopyDownloadEnabledSnapshot),
	file: version?.file
		? {
				id: version.file.id,
				name: version.file.name,
				mime: version.file.mime,
				size: version.file.size,
			}
		: null,
});

const normalizeTranslation = (translation = {}) => {
	const language = String(translation?.language || '').trim().toLowerCase();
	const fallback = DOCUMENT_TRANSLATION_LANGUAGES.find((entry) => entry.language === language);
	return {
		id: translation?.id ?? null,
		language,
		label: translation?.label ?? fallback?.label ?? language.toUpperCase(),
		shortLabel: fallback?.shortLabel ?? language.toUpperCase(),
		status: translation?.status ?? 'pending',
		file: translation?.file
			? {
					id: translation.file.id,
					name: translation.file.name,
					mime: translation.file.mime,
					size: translation.file.size,
				}
			: null,
		sourceFileHash: translation?.sourceFileHash ?? '',
		model: translation?.model ?? '',
		promptVersion: translation?.promptVersion ?? '',
		generatedAt: translation?.generatedAt ?? null,
		errorMessage: translation?.errorMessage ?? '',
		createdAt: translation?.createdAt ?? null,
		updatedAt: translation?.updatedAt ?? null,
	};
};

const normalizeTranslations = (translations = []) => {
	const byLanguage = new Map();
	(Array.isArray(translations) ? translations : []).forEach((translation) => {
		const normalized = normalizeTranslation(translation);
		if (normalized.language) byLanguage.set(normalized.language, normalized);
	});
	return DOCUMENT_TRANSLATION_LANGUAGES.map((language) => ({
		...language,
		id: null,
		status: 'pending',
		file: null,
		sourceFileHash: '',
		model: '',
		promptVersion: '',
		generatedAt: null,
		errorMessage: '',
		createdAt: null,
		updatedAt: null,
		...(byLanguage.get(language.language) || {}),
	}));
};

const normalizeSignatureStyle = (style = {}) => ({
	id: style?.id ?? '',
	label: style?.label ?? '',
	previewSvg: style?.previewSvg ?? '',
});

const normalizeSignatureProfile = (profile = {}) => {
	const availableStyles = Array.isArray(profile?.availableStyles)
		? profile.availableStyles.map(normalizeSignatureStyle)
		: [];
	const signatureStyleId =
		String(profile?.signatureStyleId || availableStyles[0]?.id || DEFAULT_SIGNATURE_STYLE_ID).trim() ||
		DEFAULT_SIGNATURE_STYLE_ID;

	return {
		id: profile?.id ?? null,
		legalName: profile?.legalName ?? '',
		signatureStyleId,
		signaturePreviewSvg: profile?.signaturePreviewSvg ?? '',
		adoptedAt: profile?.adoptedAt ?? null,
		eSignConsentAt: profile?.eSignConsentAt ?? null,
		phoneE164: profile?.phoneE164 ?? '',
		isComplete: Boolean(profile?.isComplete),
		availableStyles,
	};
};

const resolveSignatureStyleId = (payload = {}, profile = null) =>
	String(
		payload?.signatureStyleId ||
			profile?.signatureStyleId ||
			profile?.availableStyles?.[0]?.id ||
			DEFAULT_SIGNATURE_STYLE_ID,
	).trim() || DEFAULT_SIGNATURE_STYLE_ID;

const normalizeCompletionMode = (mode) =>
	mode === 'bilateral_signature' ? 'bilateral_signature' : 'read_only';

const normalizeDocumentTitle = (doc = {}) => {
	const title = String(doc?.title || '').trim();
	if (
		doc?.templateKey === ASSIGNMENT_CONFIRMATION_TEMPLATE_KEY &&
		title.toLocaleLowerCase() === 'assignment confirmation'
	) {
		return 'Assignment confirmation';
	}
	return title;
};

const normalizeDocument = (doc = {}) => {
	const completionMode = normalizeCompletionMode(doc?.completionMode);
	return {
		id: doc?.id ?? null,
		title: normalizeDocumentTitle(doc),
		description: doc?.description ?? '',
		category: normalizeSupplierDocumentCategory(doc?.category),
		sourceType: normalizeSupplierDocumentSourceType(doc?.sourceType),
		templateKey: doc?.templateKey ?? null,
		content: doc?.content && typeof doc.content === 'object' ? doc.content : null,
		metadata: doc?.metadata && typeof doc.metadata === 'object' ? doc.metadata : {},
		legacyClientDocumentId: doc?.legacyClientDocumentId ?? null,
		requiresAcceptance: completionMode === 'bilateral_signature',
		completionMode,
		requiresEffectiveDate:
			completionMode === 'bilateral_signature' && Boolean(doc?.requiresEffectiveDate),
		ownerSignsFirst: completionMode === 'bilateral_signature' && Boolean(doc?.ownerSignsFirst),
		executedCopyDownloadEnabled:
			completionMode === 'bilateral_signature' && Boolean(doc?.executedCopyDownloadEnabled),
		isArchived: Boolean(doc?.isArchived),
		archivedAt: doc?.archivedAt ?? null,
		sortOrder: doc?.sortOrder ?? 0,
		file: doc?.file
			? {
					id: doc.file.id,
					name: doc.file.name,
					mime: doc.file.mime,
					size: doc.file.size,
				}
			: null,
		currentVersion: doc?.currentVersion ? normalizeVersion(doc.currentVersion) : null,
		versions: Array.isArray(doc?.versions) ? doc.versions.map(normalizeVersion) : [],
		translations: normalizeTranslations(doc?.translations || []),
		supplierIds: Array.isArray(doc?.supplierIds) ? doc.supplierIds : [],
		supplierNames: Array.isArray(doc?.supplierNames)
			? doc.supplierNames.map((name) => String(name || '').trim()).filter(Boolean)
			: [],
		signingSummary: {
			totalExecutions: Number(doc?.signingSummary?.totalExecutions ?? 0) || 0,
			pendingExecutions: Number(doc?.signingSummary?.pendingExecutions ?? 0) || 0,
			awaitingSupplierExecutions:
				Number(doc?.signingSummary?.awaitingSupplierExecutions ?? 0) || 0,
			completedExecutions: Number(doc?.signingSummary?.completedExecutions ?? 0) || 0,
			supersededExecutions: Number(doc?.signingSummary?.supersededExecutions ?? 0) || 0,
			cancelledExecutions: Number(doc?.signingSummary?.cancelledExecutions ?? 0) || 0,
		},
		createdAt: doc?.createdAt ?? null,
		updatedAt: doc?.updatedAt ?? null,
	};
};

const upsertById = (list, item) => {
	if (!item?.id) return list;
	const next = Array.isArray(list) ? [...list] : [];
	const index = next.findIndex((entry) => String(entry?.id) === String(item.id));
	if (index >= 0) {
		next.splice(index, 1, item);
		return next;
	}
	next.push(item);
	return next;
};

const sortDocuments = (list) => {
	const next = Array.isArray(list) ? [...list] : [];
	return next.sort((a, b) => {
		const aArchived = a?.isArchived ? 1 : 0;
		const bArchived = b?.isArchived ? 1 : 0;
		if (aArchived !== bArchived) return aArchived - bArchived;
		const aOrder = Number(a?.sortOrder ?? 0);
		const bOrder = Number(b?.sortOrder ?? 0);
		if (aOrder !== bOrder) return aOrder - bOrder;
		return String(a?.title || '').localeCompare(String(b?.title || ''));
	});
};

export const useSupplierPortalAdminStore = defineStore('supplierPortalAdmin', {
	state: () => ({
		documents: [],
		loading: false,
		error: null,
		signingProfile: null,
		signingProfileLoading: false,
	}),

	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		resetError() {
			this.error = null;
		},

		findDocumentById(documentId) {
			if (documentId === null || documentId === undefined || documentId === '') return null;
			return (
				(this.documents || []).find(
					(document) => String(document?.id) === String(documentId),
				) || null
			);
		},

		setSigningProfile(profile) {
			this.signingProfile = profile ? normalizeSignatureProfile(profile) : null;
			return this.signingProfile;
		},

		// Reads the documents without touching the list, so a page can decide whether it is safe
		// to show what changed elsewhere.
		async peekDocuments() {
			const response = await axios.get(`${STRAPI_URL}/api/supplier-portal/admin/documents`, {
				headers: this.authHeaders(),
			});
			const raw = response?.data?.documents || [];
			return sortDocuments(Array.isArray(raw) ? raw.map(normalizeDocument) : []);
		},

		async fetchDocuments() {
			try {
				this.loading = true;
				this.error = null;
				this.documents = await this.peekDocuments();
				return this.documents;
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to load portal documents');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async fetchDocument(documentId, { force = false } = {}) {
			if (documentId === null || documentId === undefined || documentId === '') {
				throw new Error('documentId is required');
			}
			const cached = this.findDocumentById(documentId);
			if (cached && !force) return cached;
			await this.fetchDocuments();
			return this.findDocumentById(documentId);
		},

		async createDocument(payload) {
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/documents`,
					payload,
					{ headers: this.authHeaders() },
				);
				const doc = normalizeDocument(response?.data?.document || {});
				this.documents = sortDocuments(upsertById(this.documents, doc));
				emitEvent('portal-documents:changed', { documentId: doc.id });
				return doc;
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to create document');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async transferAssignmentFromClient(clientDocumentId) {
			if (clientDocumentId === null || clientDocumentId === undefined || clientDocumentId === '') {
				throw new Error('clientDocumentId is required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/client-documents/${encodeURIComponent(
						clientDocumentId,
					)}/transfer`,
					{},
					{ headers: this.authHeaders() },
				);
				const data = response?.data || {};
				const document = normalizeDocument(data.document || {});
				if (!document?.id) {
					throw new Error('Transferred supplier document was not returned');
				}
				this.documents = sortDocuments(upsertById(this.documents, document));
				emitEvent('portal-documents:changed', {
					documentId: document.id,
					legacyClientDocumentId: String(clientDocumentId),
					transferred: true,
					alreadyTransferred: Boolean(data.alreadyTransferred),
				});
				return {
					...data,
					document,
				};
			} catch (error) {
				this.error = normalizeErrorMessage(
					error,
					'Failed to transfer assignment confirmation',
				);
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async updateDocument(documentId, payload) {
			if (!documentId) {
				throw new Error('documentId is required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.put(
					`${STRAPI_URL}/api/supplier-portal/admin/documents/${encodeURIComponent(documentId)}`,
					payload,
					{ headers: this.authHeaders() },
				);
				const doc = normalizeDocument(response?.data?.document || {});
				this.documents = sortDocuments(upsertById(this.documents, doc));
				emitEvent('portal-documents:changed', { documentId: doc.id });
				return doc;
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to update document');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		mergeDocumentTranslations(documentId, translations = []) {
			if (!documentId) return normalizeTranslations(translations);
			const normalizedTranslations = normalizeTranslations(translations);
			this.documents = sortDocuments(
				(this.documents || []).map((doc) =>
					String(doc?.id) === String(documentId)
						? { ...doc, translations: normalizedTranslations }
						: doc,
				),
			);
			return normalizedTranslations;
		},

		async fetchDocumentTranslations(documentId, { silent = false } = {}) {
			if (!documentId) {
				throw new Error('documentId is required');
			}
			try {
				if (!silent) {
					this.loading = true;
				}
				this.error = null;
				const response = await axios.get(
					`${STRAPI_URL}/api/supplier-portal/admin/documents/${encodeURIComponent(
						documentId,
					)}/translations`,
					{ headers: this.authHeaders() },
				);
				return this.mergeDocumentTranslations(documentId, response?.data?.translations || []);
			} catch (error) {
				if (!silent) {
					this.error = normalizeErrorMessage(error, 'Failed to load translations');
				}
				throw error;
			} finally {
				if (!silent) {
					this.loading = false;
				}
			}
		},

		async regenerateDocumentTranslation({ documentId, language }) {
			if (!documentId || !language) {
				throw new Error('documentId and language are required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/documents/${encodeURIComponent(
						documentId,
					)}/translations/${encodeURIComponent(language)}/regenerate`,
					{},
					{ headers: this.authHeaders() },
				);
				const translations = this.mergeDocumentTranslations(
					documentId,
					response?.data?.translations || [response?.data?.translation].filter(Boolean),
				);
				emitEvent('portal-documents:changed', {
					documentId,
					language,
					translationRegenerated: true,
				});
				return translations;
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to regenerate translation');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async getDocumentTranslationSignedUrl({ documentId, language }) {
			if (!documentId || !language) {
				throw new Error('documentId and language are required');
			}
			try {
				this.error = null;
				const response = await axios.get(
					`${STRAPI_URL}/api/supplier-portal/admin/documents/${encodeURIComponent(
						documentId,
					)}/translations/${encodeURIComponent(language)}/signed-url`,
					{ headers: this.authHeaders() },
				);
				return response?.data || {};
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to open translation');
				throw error;
			}
		},

		async deleteDocument(documentId) {
			return this.archiveDocument(documentId);
		},

		async archiveDocument(documentId) {
			if (!documentId) {
				throw new Error('documentId is required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.delete(
					`${STRAPI_URL}/api/supplier-portal/admin/documents/${encodeURIComponent(documentId)}`,
					{ headers: this.authHeaders() },
				);
				const archived = normalizeDocument(response?.data?.document || {});
				if (archived?.id) {
					this.documents = sortDocuments(upsertById(this.documents, archived));
				} else {
					this.documents = sortDocuments(
						upsertById(this.documents, {
							...(this.documents || []).find(
								(doc) => String(doc?.id) === String(documentId),
							),
							id: documentId,
							isArchived: true,
							archivedAt: new Date().toISOString(),
						}),
					);
				}
				emitEvent('portal-documents:changed', { documentId, archived: true });
				return response?.data || { ok: true };
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to archive document');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async attachDocumentToSupplier({ supplierId, documentId }) {
			if (!supplierId || !documentId) {
				throw new Error('supplierId and documentId are required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/suppliers/${encodeURIComponent(
						supplierId,
					)}/documents/${encodeURIComponent(documentId)}/attach`,
					{},
					{ headers: this.authHeaders() },
				);
				const doc = normalizeDocument(response?.data?.document || {});
				this.documents = sortDocuments(upsertById(this.documents, doc));
				emitEvent('portal-documents:changed', { documentId: doc.id, supplierId });
				return response?.data;
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to attach document');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async detachDocumentFromSupplier({ supplierId, documentId }) {
			if (!supplierId || !documentId) {
				throw new Error('supplierId and documentId are required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/suppliers/${encodeURIComponent(
						supplierId,
					)}/documents/${encodeURIComponent(documentId)}/detach`,
					{},
					{ headers: this.authHeaders() },
				);
				const doc = normalizeDocument(response?.data?.document || {});
				this.documents = sortDocuments(upsertById(this.documents, doc));
				emitEvent('portal-documents:changed', { documentId: doc.id, supplierId });
				return response?.data;
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to detach document');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async updateSupplierMobile({ supplierId, phoneE164, mobileEnabled }) {
			if (!supplierId) {
				throw new Error('supplierId is required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/suppliers/${encodeURIComponent(
						supplierId,
					)}/mobile`,
					{ phoneE164, mobileEnabled },
					{ headers: this.authHeaders() },
				);
				return response?.data || {};
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to update mobile settings');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async sendSupplierPortalLink({ supplierId }) {
			if (!supplierId) {
				throw new Error('supplierId is required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/suppliers/${encodeURIComponent(
						supplierId,
					)}/send-portal-link`,
					{},
					{ headers: this.authHeaders() },
				);
				return response?.data || {};
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to send portal link');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async fetchSigningProfile() {
			try {
				this.signingProfileLoading = true;
				this.error = null;
				const response = await axios.get(
					`${STRAPI_URL}/api/supplier-portal/admin/signing-profile`,
					{ headers: this.authHeaders() },
				);
				return this.setSigningProfile(response?.data?.signingProfile || null);
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to load signing profile');
				throw error;
			} finally {
				this.signingProfileLoading = false;
			}
		},

		async saveSigningProfile(payload) {
			try {
				this.signingProfileLoading = true;
				this.error = null;
				const requestPayload = {
					...payload,
					signatureStyleId: resolveSignatureStyleId(payload, this.signingProfile),
				};
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/signing-profile`,
					requestPayload,
					{ headers: this.authHeaders() },
				);
				return this.setSigningProfile(response?.data?.signingProfile || null);
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to save signing profile');
				throw error;
			} finally {
				this.signingProfileLoading = false;
			}
		},

		async requestOwnerSignOtp({ supplierId, documentId, effectiveDate = null }) {
			if (!supplierId || !documentId) {
				throw new Error('supplierId and documentId are required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/suppliers/${encodeURIComponent(
						supplierId,
					)}/documents/${encodeURIComponent(documentId)}/request-sign-otp`,
					{ effectiveDate: effectiveDate || null },
					{ headers: this.authHeaders() },
				);
				emitEvent('portal-documents:changed', { supplierId, documentId, requestOtp: true });
				return response?.data?.execution || null;
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to request signature OTP');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async signExecution({ executionId, code }) {
			if (!executionId) {
				throw new Error('executionId is required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.post(
					`${STRAPI_URL}/api/supplier-portal/admin/executions/${encodeURIComponent(
						executionId,
					)}/sign`,
					{ code },
					{ headers: this.authHeaders() },
				);
				emitEvent('portal-documents:changed', { executionId, signed: true });
				return response?.data?.execution || null;
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to sign document');
				throw error;
			} finally {
				this.loading = false;
			}
		},

		async getExecutionDownloadUrl(executionId) {
			if (!executionId) {
				throw new Error('executionId is required');
			}
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.get(
					`${STRAPI_URL}/api/supplier-portal/admin/executions/${encodeURIComponent(
						executionId,
					)}/executed-url`,
					{ headers: this.authHeaders() },
				);
				return response?.data || {};
			} catch (error) {
				this.error = normalizeErrorMessage(error, 'Failed to load executed copy');
				throw error;
			} finally {
				this.loading = false;
			}
		},
	},
});
